import React, { MouseEvent } from "react";
import clsx from "clsx";
import { Col } from "react-bootstrap";

import { Button, Tooltip } from "components";

import { ReactComponent as ArrowIcon } from "static/images/stepperArrow.svg";
import styles from "./stepBar.module.scss";

type Props = {
	currentStep: number;
	steps: {
		title: string;
		img?;
		isDisabled?: boolean;
		tooltip?: string;
	}[];
	setStep?: React.Dispatch<React.SetStateAction<number>>;
	showNavigationArrows?: boolean;
	prevArrowClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
	nextArrowClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
};

type ArrowProps = {
	direction?: "previous" | "next";
	disabled?: boolean;
	onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
};

export const StepBar = ({
	steps,
	setStep,
	currentStep,
	showNavigationArrows = false,
	prevArrowClick,
	nextArrowClick,
}: Props) => {
	const Arrow = ({
		direction = "next",
		disabled = false,
		onClick,
	}: ArrowProps) => {
		const isPrev = direction === "previous";
		const onClickHandler = (
			e: React.MouseEvent<Element, MouseEvent>,
		): void => {
			if (!disabled) onClick?.(e);
		};
		return (
			<Button
				icon={
					<ArrowIcon
						className={clsx(isPrev && "flip")}
						width="20px"
					/>
				}
				btnIcon
				variant="secondary"
				title={isPrev ? "Previous step" : "Next step"}
				onClick={onClickHandler}
				disabled={disabled}
				className={styles.arrowBtn}
			/>
		);
	};

	return (
		<div className={clsx(styles.stepper, "d-flex")}>
			{showNavigationArrows && (
				<>
					<Arrow
						direction="previous"
						disabled={!prevArrowClick}
						onClick={prevArrowClick}
					/>
					<span className={styles.arrowSeparator}></span>
				</>
			)}
			{steps.map((step, index) => (
				<Tooltip
					title={step?.tooltip}
					disabled={!step?.tooltip || !step?.isDisabled}
					className={clsx(styles.stepTooltip, "col")}
					key={`tooltip-${step.title}-${index}`}
				>
					<Col
						key={`${step.title}-${index}`}
						className={clsx(
							styles.step,
							index === 0 && styles.leftMost,
							index === steps.length - 1 && styles.rightMost,
							index === currentStep
								? styles.current
								: index < currentStep
								? styles.active
								: styles.inactive,

							"d-flex",
							"align-items-center",
							"flex-column",
						)}
					>
						<div
							onClick={() => setStep?.(+index)}
							role={setStep && "button"}
							className={`${styles.wrapStepNumber} ${
								step.isDisabled && styles.isDisabled
							}`}
						>
							<div
								className={`${styles.stepNumber} d-flex justify-content-center align-items-center`}
							>
								{step.img ? (
									<img src={step.img} alt="" />
								) : (
									<span className="pb-1">{index + 1}</span>
								)}
							</div>
						</div>
						<div className={styles.title}>{step.title}</div>
					</Col>
				</Tooltip>
			))}
			{showNavigationArrows && (
				<>
					<span className={styles.arrowSeparator}></span>
					<Arrow
						disabled={!nextArrowClick}
						onClick={nextArrowClick}
					/>
				</>
			)}
		</div>
	);
};
